'use client'

import { useToast } from './use-toast'
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from './toast'
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useTranslation } from 'react-i18next'

interface ToasterProps {
  /** Whether the layout is RTL */
  isRTL?: boolean
}

/**
 * Get icon for toast variant
 */
function getToastIcon(variant?: 'default' | 'destructive' | 'warning' | null) {
  switch (variant) {
    case 'destructive':
      return <XCircle className="h-5 w-5 text-destructive-foreground" />
    case 'warning':
      return <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
    default:
      return <CheckCircle2 className="h-5 w-5 text-emerald-500" />
  }
}

export function Toaster({ isRTL = false }: ToasterProps) {
  const { toasts } = useToast()
  const { t } = useTranslation()

  // Check RTL from prop or document direction
  const effectiveRTL = isRTL || (typeof document !== 'undefined' && document.documentElement.dir === 'rtl')

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, variant, ...props }) {
        const translatedTitle = title && typeof title === 'string' ? t(title, { defaultValue: title }) : title
        const translatedDescription = description && typeof description === 'string' ? t(description, { defaultValue: description }) : description
        return (
          <Toast key={id} variant={variant} {...props}>
            <div className="flex gap-3 w-full min-w-0">
              {/* Icon */}
              <div className="flex-shrink-0 pt-0.5">
                {getToastIcon(variant)}
              </div>

              {/* Content — action goes inside here, below description */}
              <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                {translatedTitle && (
                  <ToastTitle className={cn(
                    effectiveRTL && 'text-right'
                  )}>
                    {translatedTitle}
                  </ToastTitle>
                )}
                {translatedDescription && (
                  <ToastDescription className={cn(
                    'whitespace-pre-wrap break-words',
                    effectiveRTL && 'text-right'
                  )}>
                    {translatedDescription}
                  </ToastDescription>
                )}
                {/* Action rendered below description, full-width */}
                {action && (
                  <div className="mt-2">
                    {action}
                  </div>
                )}
              </div>
            </div>
            <ToastClose />
          </Toast>
        )
      })}
      <ToastViewport className={cn(
        effectiveRTL && 'sm:left-0 sm:right-auto'
      )} />
    </ToastProvider>
  )
}
